// External dependencies.
import { LitElement, html } from "lit";
import { __ } from "@wordpress/i18n";
import swal from "sweetalert";

/**
 * AppContactForm component class.
 *
 * @since 0.11.0
 * @class AppContactForm
 * @extends {LitElement}
 */
class AppContactForm extends LitElement {
    /**
     * Creates an instance of AppContactForm.
     * 
     * @since 0.11.0
     * @memberof AppContactForm
     */
    constructor() {
        super();
        this.isSubmitting = false;
        this.errors = {};
        this.handleSubmit = this.handleSubmit.bind( this );
        this.handleSuccess = this.handleSuccess.bind( this );
        this.handleError = this.handleError.bind( this );
    }

    /**
     * Inits the component's properties.
     *
     * @since 0.11.0
     * @readonly
     * @static
     * @memberof AppContactForm
     */
    static get properties() {
        return {
            recaptchaSiteKey: { type: String },
            nonce: { type: String },
            isSubmitting: { type: Boolean },
            errors: { type: Object },
        };
    }

    /**
     * Handles the form element submit event.
     *
     * @since 0.11.0
     * @param object e
     * @memberof AppContactForm
     */
    handleSubmit( e ) {
        e.preventDefault();
        this.isSubmitting = true;
        const form = e.target;
        const url = form.getAttribute( 'action' );
        const formdata = new FormData( form );

        fetch( url, {
            method: 'POST',
            body: formdata,
        } )
            .then( res => res.json() )
            .then( this.handleSuccess )
            .catch( this.handleError );
    }

    /**
     * Handles the form success action.
     *
     * @since 0.11.0
     * @param object res
     * @memberof AppContactForm
     */
    handleSuccess( res ) {
        this.isSubmitting = false;
        grecaptcha.reset();

        if ( res.success ) {
            this.errors = {};
            this.querySelector( 'form' ).reset();

            swal( {
                title: window.app.name,
                text: res.data,
                icon: 'success',
                buttons: {
                    confirm: {
                        className: 'btn btn-ts-primary',
                    },
                },
            } );

            return;
        }

        if ( res.data.has_errors ) {
            this.errors = res.data.errors;
            return;
        }

        const text = ( 0 === res.data ) ? window.app.error_message : res.data;

        swal( {
            title: window.app.name,
            text: text,
            icon: 'warning',
            buttons: {
                confirm: {
                    className: 'btn btn-ts-primary',
                },
            },
        } );
    }

    /**
     * Handles the form error action.
     *
     * @since 0.11.0
     * @param object err
     * @memberof AppContactForm
     */
    handleError( err ) {
        console.log( err );
        this.isSubmitting = false;
        grecaptcha.reset();

        swal( {
            title: window.app.name,
            text: window.app.error_message,
            icon: 'error',
            buttons: {
                confirm: {
                    className: 'btn btn-ts-primary',
                }
            }
        } );
    }

    /**
     * Renders the component.
     *
     * @since 0.11.0
     * @since 1.3.0 Fullname field divided into firstname and lastname fields.
     * @returns TemplateResult.
     * @memberof AppContactForm
     */
    render() {
        return html`
            <form action="${window.app.ajax_url}" method="POST" @submit="${this.handleSubmit}">
                <input type="hidden" name="_wpnonce" value="${this.nonce}">
                <input type="hidden" name="action" value="handle_contact_form">
                <div class="form-row">
                    <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6">
                        <div class="form-group">
                            <label class="sr-only" for="firstname">${__( 'Nombre', 'tio-simon-foods' )}</label>
                            <input type="text" class="form-control text-center" name="firstname" id="firstname" minlength="2" placeholder="${__( 'Nombre', 'tio-simon-foods' )}" required>
                            ${this.errors.firstname ? html`<div class="form-error">${this.errors.firstname}</div>` : null}
                        </div>
                    </div>
                    <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6">
                        <div class="form-group">
                            <label class="sr-only" for="lastname">${__( 'Apellido', 'tio-simon-foods' )}</label>
                            <input type="text" class="form-control text-center" name="lastname" id="lastname" minlength="2" placeholder="${__( 'Apellido', 'tio-simon-foods' )}" required>
                            ${this.errors.lastname ? html`<div class="form-error">${this.errors.lastname}</div>` : null}
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label class="sr-only" for="email">${__( 'Email', 'tio-simon-foods' )}</label>
                    <input type="email" class="form-control text-center" name="email" id="email" placeholder="${__( 'Email', 'tio-simon-foods' )}" required>
                    ${this.errors.email ? html`<div class="form-error">${this.errors.email}</div>` : null}
                </div>
                <div class="form-group">
                    <label class="sr-only" for="phone">${__( 'Nro. de Teléfono', 'tio-simon-foods' )}</label>
                    <input type="tel" class="form-control text-center" name="phone" id="phone" pattern="^[0-9]{6,}$" placeholder="${__( 'Nro. de Teléfono', 'tio-simon-foods' )}" required>
                    ${this.errors.phone ? html`<div class="form-error">${this.errors.phone}</div>` : null}
                </div>
                <div class="form-group">
                    <label class="sr-only" for="message">${__( 'Mensaje', 'tio-simon-foods' )}</label>
                    <textarea class="form-control text-center" name="message" id="message" rows="5" minlength="10" placeholder="${__( 'Mensaje', 'tio-simon-foods' )}" required></textarea>
                    ${this.errors.message ? html`<div class="form-error">${this.errors.message}</div>` : null}
                </div>
                <div class="row align-items-lg-center">
                    <div class="col-12 col-sm-12 col-md-12 col-lg-7 col-xl-6">
                        <div class="g-recaptcha mb-3 mb-lg-0" data-sitekey="${this.recaptchaSiteKey}"></div>
                    </div>
                    <div class="col-12 col-sm-12 col-md-12 col-lg-5 col-xl-6">
                        <button type="submit" class="btn btn-ts-primary btn-lg btn-block" ?disabled="${this.isSubmitting}">
                            ${this.isSubmitting ? __( 'Envíando...', 'tio-simon-foods' ) : __( 'Enviar', 'tio-simon-foods' )}
                        </button>
                    </div>
                </div>
            </form>
        `;
    }

    /**
     * Renders the template in the light DOM.
     *
     * @since 0.11.0
     * @returns this.
     * @memberof AppContactForm
     */
    createRenderRoot() {
        return this;
    }
}

window.customElements.define( 'app-contact-form', AppContactForm );
